// TechnicianReportPDF.js
import {
  Circle,
  Document,
  Font,
  Image,
  Line,
  Link,
  Page,
  Path,
  Rect,
  Stop,
  StyleSheet,
  Svg,
  Text,
  View,
  LinearGradient,
  Defs,
} from "@react-pdf/renderer";
import React from "react";
import facebook from '../../../src/images/homepage/footer/facebook.png';
import instagram from '../../../src/images/homepage/footer/instagram.png';
import twitter from '../../../src/images/homepage/footer/twitter.png';
import youtube from '../../../src/images/homepage/footer/youtube.png';
import { default as footerLogo, default as logo } from '../../../src/images/pdf/nav.png';
import logoDark from '../../../src/images/pdf/nav-dark.png';
import car from "../../../src/images/progress/car.png";
import tyreDiagramImg from "../../../src/images/pdf/tyre.png";

// ---------------------------------------------------------------------------
// Fonts
// ---------------------------------------------------------------------------

Font.register({
  family: 'Poppins',
  src: require('../../../src/font/Poppins-Regular.ttf'),
});

Font.register({
  family: 'PoppinsS',
  src: require('../../../src/font/Poppins-SemiBold.ttf'),
});

Font.register({
  family: 'Clash',
  src: require('../../../src/font/ClashGrotesk.ttf'),
});

// ---------------------------------------------------------------------------
// CarvetPro palette — identical to the PDI report template (same design
// system, same component library below), so both reports look like one
// product family.
// ---------------------------------------------------------------------------

const LIME = '#ADD144';
const LIME_TINT = '#F0FFC5';
const DARK = '#1F1F1F';
const CARD_BG = '#E9E8E8';
const CARD_BORDER = '#DDDDDD';
const TEXT_BODY = '#333333';
const TEXT_LABEL = '#777777';
const TEXT_VALUE = '#1F1F1F';

const STATUS = {
  excellent: { label: 'EXCELLENT', bg: '#E8F5E1', color: '#1F8A3D' },
  average: { label: 'AVERAGE', bg: '#FFF6D9', color: '#B98900' },
  poor: { label: 'POOR', bg: '#FFF1DE', color: '#D97706' },
  critical: { label: 'CRITICAL', bg: '#FDE8E8', color: '#DC2626' },
  attention: { label: 'NEEDS ATTENTION', bg: '#FFF1DE', color: '#D97706' },
};

const tierFromScore = (score) => {
  if (score >= 90) return STATUS.excellent;
  if (score >= 75) return STATUS.average;
  if (score >= 60) return STATUS.poor;
  return STATUS.critical;
};

// Title-cased label for prose use (e.g. "31.21% · Critical"), always derived
// from the same 4-tier tierFromScore boundaries as the badge color — kept as
// one source of truth so the label and badge tier can never disagree.
const tierLabelTitleCase = (score) => {
  const label = tierFromScore(score).label;
  return label.charAt(0) + label.slice(1).toLowerCase();
};

// A zone's value may be a plain string/boolean (most zones) OR an array of
// {label,value} objects (a handful of used-car-only fields — Condition Of
// Chassis Number, some plastic/exterior conditions — use react-select
// multi-pickers on the technician form). Both need to read as "flagged".
const hasContent = (v) => {
  if (Array.isArray(v)) return v.length > 0;
  if (v && typeof v === 'object') return Object.keys(v).length > 0;
  return !!v;
};
const displayValue = (v) => {
  if (Array.isArray(v)) return v.map((item) => (typeof item === 'string' ? item : item?.label || item?.value || '')).join(', ');
  if (v && typeof v === 'object') return v.label || v.value || '';
  return v;
};
const zoneTier = (value) => (hasContent(value) ? STATUS.attention : STATUS.excellent);

// There's no dedicated short booking-reference field anywhere in the data
// model (confirmed against the Service schema) — the Mongo _id (24 hex
// chars) is genuinely the only identifier that exists. Shown as the last 8
// characters (the most-random tail of an ObjectId, so distinct bookings
// stay distinguishable) with a # prefix, keeping the display at ≤10 chars.
const shortBookingId = (id) => (id ? `#${String(id).slice(-8).toUpperCase()}` : null);

// ---------------------------------------------------------------------------
// Styles — identical to the PDI report template
// ---------------------------------------------------------------------------

const styles = StyleSheet.create({
  page: {
    fontSize: 10,
    backgroundColor: "#ffffff",
    fontFamily: 'Poppins',
    paddingBottom: 40,
  },
  darkPage: {
    fontSize: 10,
    backgroundColor: DARK,
    fontFamily: 'Poppins',
  },

  pageHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 36,
    paddingTop: 24,
    paddingBottom: 12,
  },
  pageHeaderLabel: {
    fontSize: 9,
    color: TEXT_LABEL,
    fontFamily: 'PoppinsS',
  },
  pageHeaderLogo: { width: 90, height: 26 },

  body: { paddingHorizontal: 36 },

  h1: { fontFamily: 'Clash', fontSize: 26, color: DARK, marginBottom: 4, maxWidth: '75%' },
  h2: { fontFamily: 'Clash', fontSize: 16, color: DARK, marginBottom: 8 },
  subtitle: { fontSize: 10, color: TEXT_LABEL, marginBottom: 18 },

  card: {
    borderWidth: 1,
    borderColor: CARD_BORDER,
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
  },
  cardGray: {
    backgroundColor: CARD_BG,
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
  },

  label: { fontSize: 8, color: TEXT_LABEL, fontFamily: 'Poppins', marginBottom: 2 },
  value: { fontSize: 11, color: TEXT_VALUE, fontFamily: 'PoppinsS' },

  row: { flexDirection: 'row' },
  grid2: { flexDirection: 'row', flexWrap: 'wrap' },
  gridCell: { width: '50%', marginBottom: 12, paddingRight: 12 },

  divider: { borderBottomWidth: 1, borderBottomColor: CARD_BORDER, marginVertical: 10 },

  badge: {
    borderRadius: 5,
    paddingHorizontal: 8,
    paddingVertical: 5,
    alignSelf: 'flex-start',
  },
  badgeText: {
    fontSize: 7,
    fontFamily: 'PoppinsS',
    letterSpacing: 0.3,
  },

  highlightRow: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 10,
    paddingVertical: 10,
    paddingHorizontal: 14,
    marginBottom: 8,
  },
  highlightDot: {
    width: 8, height: 8, borderRadius: 4, marginRight: 10,
  },
  highlightText: { fontSize: 10, color: TEXT_BODY, flex: 1 },

  scoreCard: {
    borderWidth: 1,
    borderColor: CARD_BORDER,
    borderRadius: 12,
    padding: 14,
    marginBottom: 12,
  },
  scoreCardHeadRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 },
  scoreCardTitle: { fontSize: 13, fontFamily: 'PoppinsS', color: DARK },
  scoreCardDesc: { fontSize: 9, color: TEXT_LABEL },

  photoGrid: { flexDirection: 'row', flexWrap: 'wrap', marginHorizontal: -6 },
  photoCell: { width: '50%', paddingHorizontal: 6, marginBottom: 16 },
  photoCell3: { width: '33.33%', paddingHorizontal: 6, marginBottom: 16 },
  photoImg: { width: '100%', height: 100, borderRadius: 8, objectFit: 'cover' },
  photoCaption: { fontSize: 8, color: TEXT_LABEL, marginTop: 4, fontFamily: 'PoppinsS' },

  microStrip: { flexDirection: 'row', flexWrap: 'wrap', marginTop: 14, paddingTop: 6 },
  microCell: { width: 64, marginRight: 8, marginBottom: 8 },
  microImg: { width: 64, height: 64, borderRadius: 6, objectFit: 'cover' },
  microCaption: { fontSize: 6, color: TEXT_LABEL, marginTop: 2, fontFamily: 'PoppinsS' },

  zoneRow: {
    borderBottomWidth: 1, borderBottomColor: '#EFEFEF', paddingVertical: 11,
  },
  zoneLabel: { fontSize: 9.5, color: TEXT_BODY, fontFamily: 'PoppinsS' },
  zoneDetail: { fontSize: 8, color: TEXT_LABEL, marginTop: 2 },

  // Reason text (why a zone is flagged): no box, no fill, no border — the
  // "Reason" label carries the same amber as the NEEDS ATTENTION badge
  // (#D97706), the detail text is plain black, both bold, one clean line.
  reasonBox: {
    marginTop: 3,
  },
  reasonLine: { fontSize: 8.5 },
  reasonLabel: { fontSize: 8.5, fontFamily: 'PoppinsS', color: '#D97706' },
  reasonText: { fontSize: 8.5, fontFamily: 'PoppinsS', color: '#1F1F1F' },

  // Technician's Remarks: one row per remark, a hairline divider between
  // rows (same rhythm as the zone rows). Text flexes to the remaining width
  // on the left; the photo, when present, is a fixed 120x90 thumbnail on the
  // right, cropped to fill via objectFit 'cover' (supported by react-pdf's
  // <Image>). No photo -> the text spans the full row width, nothing
  // reserved. No leading numerals — the row/divider structure organises the
  // list and order is preserved by the array order. 16pt vertical padding on
  // every row. Each row is one wrap={false} unit so a remark and its photo
  // never split across a page.
  remarkRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 16,
    borderBottomWidth: 0.5,
    borderBottomColor: '#EFEFEF',
  },
  remarkRowLast: { borderBottomWidth: 0 },
  remarkText: { fontSize: 9, color: TEXT_BODY, flex: 1, paddingRight: 14, lineHeight: 1.45 },
  remarkPhoto: {
    width: 120,
    height: 90,
    borderRadius: 6,
    objectFit: 'cover',
    borderWidth: 1,
    borderColor: CARD_BORDER,
  },

  legendRow: { flexDirection: 'row', marginTop: 22 },
  legendCell: { flex: 1, paddingRight: 10 },
  legendBadge: { alignSelf: 'flex-start', marginBottom: 4 },
  legendDesc: { fontSize: 7.5, color: TEXT_LABEL },

  footnote: {
    borderStyle: 'dashed',
    borderWidth: 1,
    borderColor: CARD_BORDER,
    borderRadius: 8,
    padding: 10,
    marginTop: 12,
  },
  footnoteText: { fontSize: 8, color: TEXT_LABEL },

  // Technician Rating rows — a 0-10 star scale, distinct from the
  // deduction-driven EXCELLENT/AVERAGE/POOR/CRITICAL badges elsewhere, so
  // this section reads visually as a different kind of assessment
  // (technician judgment, not an outcomes-based deduction score). Kept
  // out of the score math entirely per the confirmed scope decision.
  ratingRow: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    borderBottomWidth: 1, borderBottomColor: '#EFEFEF', paddingVertical: 10,
  },
  ratingLabel: { fontSize: 9.5, color: TEXT_BODY, fontFamily: 'PoppinsS' },

  footerContainer: {
    backgroundColor: DARK,
    color: '#FFFFFF',
    padding: 24,
    marginTop: 30,
  },
  contentWrapper: { flexDirection: 'row', justifyContent: 'space-between', flexWrap: 'wrap' },
  sectionss: { width: '23%', marginBottom: 20 },
  // Sized to match the tagline text block directly below it ("Your
  // trusted partner..."), not the full column box. Measured from the
  // rendered tagline's first line ("Your trusted partner for ex-"):
  // spans 24.05pt-144.91pt, ~121pt wide. Logo's left edge already sits at
  // the column's left edge (24pt, same as the tagline's own left edge),
  // so only the width needed adjusting. 121x34 keeps the 267x76 asset ratio.
  footerLogo: { width: 121, height: 34, marginBottom: 10 },
  description: { fontSize: 9, color: '#D1D1D1', marginBottom: 15 },
  socialIcons: { flexDirection: 'row', gap: 5 },
  iconWrapper: { padding: 4 },
  socialIcon: { width: 13, height: 13 },
  sectionHeading: { fontSize: 10, fontFamily: 'PoppinsS', marginBottom: 8, color: LIME },
  listItem: { fontSize: 8.5, marginBottom: 5, color: '#EDEDED' },
  dividers: { borderTop: '1px solid #3A3A3A', marginVertical: 10 },
  copyright: { fontSize: 7.5, textAlign: 'center', color: '#AAAAAA' },
});

// ---------------------------------------------------------------------------
// Small components — identical to the PDI report template
// ---------------------------------------------------------------------------

const PageHeader = ({ pageLabel }) => (
  <View style={styles.pageHeader} fixed>
    <Text style={styles.pageHeaderLabel}>{pageLabel || 'Used Car Inspection Report'}</Text>
    <Image style={styles.pageHeaderLogo} src={logoDark} />
  </View>
);

const Badge = ({ tier }) => (
  <View style={[styles.badge, { backgroundColor: tier.bg }]}>
    <Text style={[styles.badgeText, { color: tier.color }]}>{tier.label}</Text>
  </View>
);

const Legend2 = () => (
  <View wrap={false} style={styles.legendRow}>
    <View style={styles.legendCell}>
      <View style={[styles.badge, styles.legendBadge, { backgroundColor: STATUS.excellent.bg }]}>
        <Text style={[styles.badgeText, { color: STATUS.excellent.color }]}>EXCELLENT</Text>
      </View>
      <Text style={styles.legendDesc}>No issue reported for this zone</Text>
    </View>
    <View style={styles.legendCell}>
      <View style={[styles.badge, styles.legendBadge, { backgroundColor: STATUS.attention.bg }]}>
        <Text style={[styles.badgeText, { color: STATUS.attention.color }]}>NEEDS ATTENTION</Text>
      </View>
      <Text style={styles.legendDesc}>Technician flagged an issue on this zone (see description)</Text>
    </View>
  </View>
);

const ZONE_BADGE_GUTTER = 104;

// `reason` (optional) separates "why this is flagged" from `value` when a
// caller also has other descriptive text that isn't itself the reason (e.g.
// Tyres' brand/variant/size string) — `value` still renders as plain detail
// text in that case, and `reason` gets the amber callout treatment on its
// own line below it. When `reason` isn't given, `value` doubles as the
// reason text for every other zone type (the common case). `reason` may
// itself be the array/{label,value} shape react-select multi-pickers use —
// run through displayValue() same as `value`.
const ZoneRow = ({ label, value, flagged, reason }) => {
  const t = flagged === undefined ? zoneTier(value) : (flagged ? STATUS.attention : STATUS.excellent);
  // displayValue() normalizes arrays/{label,value} objects to a string, but
  // a bare boolean (System Observations / Fluid Inspection's pure Yes/No
  // toggles — no reason data exists for those) passes through unchanged —
  // guard both against rendering an empty amber box with just "REASON:"
  // and nothing after it.
  const rawText = displayValue(value);
  const text = typeof rawText === 'string' ? rawText : '';
  const isFlagged = flagged === undefined ? hasContent(value) : flagged;
  const rawReason = reason !== undefined ? displayValue(reason) : rawText;
  const reasonText = typeof rawReason === 'string' ? rawReason : '';
  const showReasonBox = isFlagged && !!reasonText;
  const showPlainDetail = !!text && (reason !== undefined || !showReasonBox);
  return (
    <View wrap={false} style={[styles.zoneRow, { position: 'relative', paddingRight: ZONE_BADGE_GUTTER }]}>
      <Text style={styles.zoneLabel}>{label}</Text>
      {showPlainDetail && <Text style={styles.zoneDetail}>{text}</Text>}
      {showReasonBox && (
        <View style={styles.reasonBox}>
          <Text style={styles.reasonLine}>
            <Text style={styles.reasonLabel}>Reason: </Text>
            <Text style={styles.reasonText}>{reasonText}</Text>
          </Text>
        </View>
      )}
      <View style={{ position: 'absolute', top: 0, bottom: 0, right: 0, justifyContent: 'center' }}>
        <Badge tier={t} />
      </View>
    </View>
  );
};

const ZoneGrid = ({ zones, useExplicitFlag }) => {
  const pairs = [];
  for (let i = 0; i < zones.length; i += 2) pairs.push([zones[i], zones[i + 1]]);
  return (
    <View>
      {pairs.map((pair, i) => (
        <View style={{ flexDirection: 'row' }} key={i} wrap={false}>
          <View style={{ width: '50%', paddingRight: 10 }}>
            <ZoneRow label={pair[0][0]} value={pair[0][1]} flagged={useExplicitFlag ? pair[0][2] : undefined} />
          </View>
          <View style={{ width: '50%', paddingRight: 10 }}>
            {pair[1] && <ZoneRow label={pair[1][0]} value={pair[1][1]} flagged={useExplicitFlag ? pair[1][2] : undefined} />}
          </View>
        </View>
      ))}
    </View>
  );
};

const PhotoCard = ({ src, caption, wide }) => {
  if (!src) return null;
  return (
    <View wrap={false} style={wide ? styles.photoCell : styles.photoCell3}>
      <Image style={styles.photoImg} src={src} />
      <Text style={styles.photoCaption}>{caption}</Text>
    </View>
  );
};

// Technician's Remarks — numbered free-text lines, each with an optional
// evidence photo the technician attached to that specific line. Reads the
// structured `technicianRemarks` array ([{ text, photoUrl }]) when present
// (server normalises older reports into it from the plain-text
// `finalObservations`); falls back to splitting `finalObservations` itself
// so a freshly-generated report that only carries the string still renders.
const remarksFromReportData = (reportData) => {
  const arr = reportData?.technicianRemarks;
  if (Array.isArray(arr) && arr.length > 0) {
    return arr
      .filter((r) => r && (typeof r.text === 'string' || r.photoUrl))
      .map((r) => ({ text: (r.text || '').trim(), photoUrl: r.photoUrl || null }))
      .filter((r) => r.text || r.photoUrl);
  }
  return String(reportData?.finalObservations || '')
    .split('\n')
    .map((line) => ({ text: line.trim(), photoUrl: null }))
    .filter((r) => r.text);
};

const TechnicianRemarks = ({ reportData }) => {
  const remarks = remarksFromReportData(reportData);
  if (remarks.length === 0) return null;
  return (
    <View style={[styles.card, { marginTop: 14 }]}>
      <Text style={styles.h2}>Technician's Remarks</Text>
      {remarks.map((r, index) => (
        <View
          key={index}
          wrap={false}
          style={[styles.remarkRow, index === remarks.length - 1 && styles.remarkRowLast]}
        >
          <Text style={styles.remarkText}>{r.text}</Text>
          {!!r.photoUrl && <Image style={styles.remarkPhoto} src={r.photoUrl} />}
        </View>
      ))}
    </View>
  );
};

const PhotoStrip = ({ zones, reportData }) => {
  const withPhotos = zones.filter(([, , fileKey]) => fileKey && reportData?.[fileKey]);
  if (withPhotos.length === 0) return null;
  return (
    <View style={styles.microStrip}>
      {withPhotos.map(([label, , fileKey]) => (
        <View wrap={false} style={styles.microCell} key={fileKey}>
          <Image style={styles.microImg} src={reportData[fileKey]} />
          <Text style={styles.microCaption}>{label}</Text>
        </View>
      ))}
    </View>
  );
};

// 5-star scale — verified against the technician form (usedCar.jsx), which
// renders <StarRatings numberOfStars={5}> for every one of these rows. The
// data was always 0-5 (default value 4); the previous 10-star render here
// was a display bug (an unverified assumption written without checking the
// source), not a real 0-10 data range — it was rendering every rating as
// visually half-filled. No value conversion needed, the number itself was
// always correct.
const STAR_PATH = "M12 2l2.9 6.3 6.9.6-5.2 4.6 1.6 6.8L12 16.9 5.8 20.3l1.6-6.8L2.2 8.9l6.9-.6L12 2z";
const StarRow = ({ value }) => {
  const raw = value && typeof value === 'object' ? value.value : value;
  const n = Math.min(Math.max(parseInt(raw, 10) || 0, 0), 5);
  return (
    <View style={{ flexDirection: 'row' }}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Svg key={i} width="13" height="13" viewBox="0 0 24 24" style={{ marginLeft: 2 }}>
          <Path d={STAR_PATH} fill={i < n ? LIME : '#E5E5E5'} />
        </Svg>
      ))}
    </View>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const UsedCarPDF = ({ reportData, serviceData, carImageUrl }) => {

  // ---- Deduction / score math (kept identical to the existing live logic
  // in the pre-redesign usedCarPdf.jsx — this round redesigns presentation
  // only, not scoring behaviour). Weights differ from the PDI report on
  // purpose: Used Car has a much larger zone list (38-item body/paint deck
  // vs PDI's 12, 9-item infotainment vs PDI's 5), so its per-zone weights
  // are proportionally smaller to keep the resulting score in a sane range
  // — this was already true in production before this redesign. ----

  function calculateTotalDeduction(states, weight) {
    const valid = states.filter((s) => hasContent(s));
    return valid.length * weight;
  }
  const bodyAndPaint = [
    reportData?.issueB, reportData?.issueFB, reportData?.issueFRQP, reportData?.issueFD,
    reportData?.issueRRD, reportData?.issueRRQP, reportData?.issueBT, reportData?.issueFLF,
    reportData?.issueFLDS, reportData?.issueRLDS, reportData?.selectedConditionSensor1,
    reportData?.selectedConditionSensor2,
    reportData?.issueHL, reportData?.issueRRB, reportData?.issueTL, reportData?.issueRB,
    reportData?.issueLRD, reportData?.issueLPB, reportData?.issueLPA, reportData?.issueRLD,
    reportData?.issueRPA, reportData?.issueCT, reportData?.issueFW, reportData?.issueUB,
    reportData?.selectedConditionB, reportData?.selectedConditionHL, reportData?.selectedConditionFL,
    reportData?.selectedConditionRRB, reportData?.selectedConditionTL, reportData?.selectedConditionRB,
    reportData?.selectedConditionLRD, reportData?.selectedConditionLPB, reportData?.selectedConditionLPA,
    reportData?.selectedConditionA, reportData?.selectedConditionFW, reportData?.selectedConditionCT,
    reportData?.selectedConditionUB, reportData?.selectedConditionRPA, reportData?.selectedConditionRPB,
    reportData?.selectedConditionRLQPS, reportData?.selectedConditionRF,
  ];
  const deductionPerState = 0.39;
  const totalDeduction = calculateTotalDeduction(bodyAndPaint, deductionPerState);

  function calculateTotalDeductionFromConditions(conditions, weight) {
    const valid = conditions.filter((c) => hasContent(c) || c === true);
    return valid.length * weight;
  }
  const enginePerformance = [
    reportData?.BatteryEnergy < 11.5,
    reportData?.LoadEngine > 35,
    reportData?.idleRPM > 1400,
  ];
  const deductionPerCondition = 0.82;
  const totalDeductionEngine = calculateTotalDeductionFromConditions(enginePerformance, deductionPerCondition);

  function calculateTotalDeductionForTrueStates(states, weight) {
    const trueStates = states.filter((s) => (Array.isArray(s) ? s.includes(true) : s === true));
    return trueStates.length * weight;
  }
  const systemObservations = [
    !reportData?.isToggledEngineCrack, !reportData?.isToggledWarning, !reportData?.isToggledSuspicious,
    !reportData?.isToggledSmoke, !reportData?.isToggledGearshift, !reportData?.isToggledBreak,
    !reportData?.isToggledSteering, !reportData?.isToggledHandbreak, !reportData?.isToggledReverse,
    !reportData?.isToggledSmooth,
  ];
  const deductionPerTrueState = 1.64;
  const totalDeductionSystemObservations = calculateTotalDeductionForTrueStates(systemObservations, deductionPerTrueState);

  function calculateTotalCarpetDeductionForTrueStates(states, weight) {
    const valid = states.filter((s) => hasContent(s));
    return valid.length * weight;
  }
  const carpetStates = [
    reportData?.selectedConditionDS, reportData?.selectedConditionCDS, reportData?.selectedConditionDRS,
    reportData?.selectedConditionCDRS, reportData?.selectedConditionBI,
  ];
  const deductionPerCarpetValidState = 0.82;
  const totalDeductionCarpet = calculateTotalCarpetDeductionForTrueStates(carpetStates, deductionPerCarpetValidState);

  function calculateTotalPanelDeductionForTrueStates(states, weight) {
    const valid = states.filter((s) => hasContent(s));
    return valid.length * weight;
  }
  const plasticPanelStates = [
    reportData?.selectedConditionPDS, reportData?.selectedConditionPCDS, reportData?.selectedConditionPDRS,
    reportData?.selectedConditionPCDRS, reportData?.selectedConditionBIS, reportData?.selectedConditionD,
    reportData?.selectedConditionGC,
  ];
  const deductionPerValidState = 0.82;
  const totalDeductionPlasticPanel = calculateTotalPanelDeductionForTrueStates(plasticPanelStates, deductionPerValidState);

  function calculateTotalSeatDeductionForTrueStates(states, weight) {
    const valid = states.filter((s) => hasContent(s));
    return valid.length * weight;
  }
  const seatStates = [
    reportData?.selectedConditionSDS, reportData?.selectedConditionSCDS, reportData?.selectedConditionSDRS,
    reportData?.selectedConditionSCDRS, reportData?.selectedConditionHR, reportData?.selectedConditionCHR,
    reportData?.selectedConditionRHR, reportData?.selectedConditionHCDR,
  ];
  const rubberComponentsStates = [
    reportData?.selectedConditionRC1, reportData?.selectedConditionRC2, reportData?.selectedConditionRC3,
    reportData?.selectedConditionRC4, reportData?.selectedConditionRC5, reportData?.selectedConditionRC6,
  ];
  const glassAndMirrorsStates = [
    reportData?.selectedConditionGM2, reportData?.selectedConditionGM3, reportData?.selectedConditionGM4,
    reportData?.selectedConditionGM6, reportData?.selectedConditionGM8, reportData?.selectedConditionGM10,
    reportData?.selectedConditionGM12, reportData?.selectedConditionGM14, reportData?.selectedConditionGM15,
    reportData?.selectedConditionGM17, reportData?.selectedConditionGM19, reportData?.selectedConditionGM21,
  ];
  const tyresStates = [
    reportData?.selectedConditionFT, reportData?.selectedConditionRTN, reportData?.selectedConditionRLN,
    reportData?.selectedConditionFLNT, reportData?.selectedConditionSparen,
  ];
  const deductionPerSeatValidState = 0.82;
  const totalDeductionSeat = calculateTotalSeatDeductionForTrueStates(seatStates, deductionPerSeatValidState);
  const totalDeductionRubberComponents = calculateTotalSeatDeductionForTrueStates(rubberComponentsStates, deductionPerSeatValidState);
  const totalDeductionGlassAndMirrors = calculateTotalSeatDeductionForTrueStates(glassAndMirrorsStates, deductionPerSeatValidState);
  const totalDeductionTyres = calculateTotalSeatDeductionForTrueStates(tyresStates, deductionPerSeatValidState);

  function calculateTotalCoreDeductionForTrueStates(states, weight) {
    const valid = states.filter((s) => (Array.isArray(s) ? s.length > 0 : (s && s !== 'Others')));
    return valid.length * weight;
  }
  const carsCoreMechanicalStates = [
    reportData?.selectedConditionCarsCore2, reportData?.selectedConditionCarsCore3, reportData?.selectedConditionCarsCore1,
    reportData?.selectedConditionCarsCore11, reportData?.selectedConditionCarsCore4, reportData?.selectedConditionCarsCore5,
    reportData?.selectedConditionCarsCore6, reportData?.selectedConditionCarsCore7, reportData?.selectedConditionCarsCore8,
    reportData?.selectedConditionCarsCore9, reportData?.selectedConditionCarsCore10, reportData?.selectedConditionCarsCore12,
  ];
  const deductionPerCoreValidState = 1.64;
  const totalDeductionCarsCoreMechanical = calculateTotalCoreDeductionForTrueStates(carsCoreMechanicalStates, deductionPerCoreValidState);

  function calculateTotalFluidDeductionForValidStates(states, exemptionStates, weight) {
    const valid = states.filter((s) => hasContent(s) && !exemptionStates.includes(s));
    return valid.length * weight;
  }
  const fluidLevelStates = [
    reportData?.isToggledFluidLevel1, reportData?.isToggledFluidLevel2, reportData?.isToggledFluidLevel3,
    reportData?.isToggledFluidLevel4, reportData?.isToggledFluidLevel5, reportData?.isToggledFluidLevel6,
    reportData?.isToggledFluidLevel7, reportData?.isToggledFluidLevel8, reportData?.isToggledFluidLevel9,
    reportData?.isToggledFluidLevel10, reportData?.isToggledFluidLevel11,
  ];
  const exemptFluidLevelStates = [
    reportData?.isToggledFluidLevel1, reportData?.isToggledFluidLevel3, reportData?.isToggledFluidLevel5,
    reportData?.isToggledFluidLevel7,
  ];
  const panelGapStates = [
    reportData?.selectedButtonGO, reportData?.selectedButtonGOBL, reportData?.selectedButtonGOFRD,
    reportData?.selectedButtonGOFLD, reportData?.selectedButtonGORRDR, reportData?.selectedButtonGORRLD,
    reportData?.selectedButtonGOBR, reportData?.selectedButtonGOBT,
  ];
  const infotainmentAndEletronicFeaturesStates = [
    reportData?.selectedConditionIS, reportData?.selectedConditionIC, reportData?.selectedConditionAB,
    reportData?.selectedConditionSASQ, reportData?.selectedConditionPW, reportData?.selectedConditionGCO,
    reportData?.selectedConditionCQ, reportData?.selectedConditionSC, reportData?.selectedConditionIL,
  ];
  const deductionPerFluidValidState = 0.456;
  const totalDeductionFluidLevel = calculateTotalFluidDeductionForValidStates(fluidLevelStates, exemptFluidLevelStates, deductionPerFluidValidState);
  const totalDeductionPanelGap = calculateTotalFluidDeductionForValidStates(panelGapStates, [], deductionPerFluidValidState);
  const totalDeductionInfotainmentAndEletronicFeatures = calculateTotalFluidDeductionForValidStates(infotainmentAndEletronicFeaturesStates, [], deductionPerFluidValidState);

  const initialPercentage = 100;
  const isOdometerConditionMet = reportData?.odometerBasic < reportData?.distKMS ? "Yes" : "No";

  function calculateScorePercentage() {
    const totalDeductions = [
      totalDeduction, totalDeductionEngine, totalDeductionSystemObservations, totalDeductionCarpet,
      totalDeductionPlasticPanel, totalDeductionSeat, totalDeductionRubberComponents, totalDeductionGlassAndMirrors,
      totalDeductionTyres, totalDeductionCarsCoreMechanical, totalDeductionFluidLevel, totalDeductionPanelGap,
      totalDeductionInfotainmentAndEletronicFeatures,
    ];
    let finalScore = initialPercentage;
    finalScore = totalDeductions.reduce((acc, d) => acc - d, finalScore);
    if (isOdometerConditionMet === "Yes") finalScore -= 0.82;
    return finalScore.toFixed(2);
  }
  const scorePercentage = calculateScorePercentage();

  const treadPercentNum = (raw) => {
    const v = parseFloat(raw);
    if (isNaN(v) || v < 0 || v > 15) return null;
    return Math.min(100, Math.round((v / 9) * 100));
  };

  const scoreBodyPaint = Math.max(0, 100 - totalDeduction - totalDeductionPanelGap);
  const scoreEngineTrans = Math.max(0, 100 - totalDeductionEngine - totalDeductionSystemObservations);
  const scoreMechFluids = Math.max(0, 100 - totalDeductionCarsCoreMechanical - totalDeductionFluidLevel);
  const scoreTyres = Math.max(0, 100 - totalDeductionTyres);
  const scoreInterior = Math.max(0, 100 - totalDeductionCarpet - totalDeductionPlasticPanel - totalDeductionSeat
    - totalDeductionRubberComponents - totalDeductionGlassAndMirrors - totalDeductionInfotainmentAndEletronicFeatures);

  // ---- Zone definitions — 26 body/paint zones (vs PDI's 13), traced from
  // the pre-redesign usedCarPdf.jsx's own table rows field-by-field. ----

  const bodyPaintZones = [
    ["Bonnet", reportData?.issueB, "paintBodyFile1", reportData?.selectedButtonB, reportData?.valueB],
    ["Headlight", reportData?.selectedConditionHL, "paintBodyFile2", reportData?.selectedButtonHL, reportData?.valueHL],
    ["Foglamps", reportData?.selectedConditionFL, "paintBodyFile3", reportData?.selectedButtonFL, reportData?.valueFL],
    ["Front Bumper", reportData?.issueFB, "paintBodyFile4", reportData?.selectedButtonFB, reportData?.valueFB],
    ["Front Right Fender", reportData?.issueFRQP, "paintBodyFile5", reportData?.selectedButtonFRQP, reportData?.valueFRQP],
    ["Right Pillar-A", reportData?.selectedConditionRPA, "paintBodyFile6", reportData?.selectedButtonRPA, reportData?.valueFRPA],
    ["Front Right Door", reportData?.issueFD, "paintBodyFile7", reportData?.selectedButtonFD, reportData?.valueFD],
    ["Right Pillar-B", reportData?.selectedConditionRPB, "paintBodyFile8", reportData?.selectedButtonRPB, reportData?.valueFRPB],
    ["Rear Right Door", reportData?.issueRRD, "paintBodyFile9", reportData?.selectedButtonRRD, reportData?.valueRRD],
    ["Right Running Board", reportData?.selectedConditionRRB, "paintBodyFile10", reportData?.selectedButtonRRB, reportData?.valueRRB],
    ["Rear Right Quarter Panel", reportData?.issueRRQP, "paintBodyFile11", reportData?.selectedButtonRRQP, reportData?.valueRRQP],
    ["Boot / Trunk", reportData?.issueBT, "paintBodyFile12", reportData?.selectedButtonBT, reportData?.valueBT],
    ["Tail Light", reportData?.selectedConditionTL, "paintBodyFile13", reportData?.selectedButtonTL, reportData?.valueTL],
    ["Rear Bumper", reportData?.selectedConditionRB, "paintBodyFile14", reportData?.selectedButtonRB, reportData?.valueRB],
    ["Left Running Board", reportData?.selectedConditionLRD, "paintBodyFile15", reportData?.selectedButtonLRD, reportData?.valueLRD],
    ["Left Pillar B", reportData?.selectedConditionLPB, "paintBodyFile16", reportData?.selectedButtonLPB, reportData?.valueLPB],
    ["Left Pillar A", reportData?.selectedConditionLPA, "paintBodyFile17", reportData?.selectedButtonLPA, reportData?.valueLPA],
    ["Apron", reportData?.selectedConditionA, "paintBodyFile18", reportData?.selectedButtonApron, reportData?.valueApron],
    ["Fire Wall", reportData?.selectedConditionFW, "paintBodyFile19", reportData?.selectedButtonFW, reportData?.valueFW],
    ["Cowltop", reportData?.selectedConditionCT, "paintBodyFile20", reportData?.selectedButtonCT, reportData?.valueCT],
    ["Under Body", reportData?.selectedConditionUB, "paintBodyFile21", reportData?.selectedButtonUB, reportData?.valueUB],
    ["Front Left Fender", reportData?.issueFLF, "paintBodyFile22", reportData?.selectedButtonFLF, reportData?.valueFLF],
    ["Front Left Door", reportData?.issueFLDS, "paintBodyFile23", reportData?.selectedButtonFLDS, reportData?.valueFLDS],
    ["Rear Left Door", reportData?.issueRLDS, "paintBodyFile24", reportData?.selectedButtonRLDS, reportData?.valueRLDS],
    ["Rear Left Quarter Panel", reportData?.selectedConditionSensor1, "paintBodyFile25", reportData?.selectedButtonRLQPS, reportData?.valueRLQPS],
    ["Roof", reportData?.selectedConditionSensor2, "paintBodyFile26", reportData?.selectedButtonRF, reportData?.valueRF],
  ];

  const panelGapZones = [
    ["Bonnet Right", reportData?.selectedButtonGO, reportData?.selectedButtonRO, reportData?.valueGRT1, reportData?.valueGRD1],
    ["Bonnet Left", reportData?.selectedButtonGOBL, reportData?.selectedButtonBL, reportData?.valueGRT2, reportData?.valueGRD2],
    ["Front Right Door", reportData?.selectedButtonGOFRD, reportData?.selectedButtonFRD, reportData?.valueGRT3, reportData?.valueGRD3],
    ["Front Left Door", reportData?.selectedButtonGOFLD, reportData?.selectedButtonFLD, reportData?.valueGRT4, reportData?.valueGRD4],
    ["Rear Right Door", reportData?.selectedButtonGORRDR, reportData?.selectedButtonRRDR, reportData?.valueGRT5, reportData?.valueGRD5],
    ["Rear Left Door", reportData?.selectedButtonGORRLD, reportData?.selectedButtonRRLR, reportData?.valueGRT6, reportData?.valueGRD6],
    ["Boot Right", reportData?.selectedButtonGOBR, reportData?.selectedButtonBRR, reportData?.valueGRT7, reportData?.valueGRD7],
    ["Boot Left", reportData?.selectedButtonGOBT, reportData?.selectedButtonBRT, reportData?.valueGRT8, reportData?.valueGRD8],
  ];

  const coreMechZones = [
    ["HVAC / AC Systems", reportData?.selectedConditionCarsCore1],
    ["Engine Systems", reportData?.selectedConditionCarsCore2],
    ["Brake Systems", reportData?.selectedConditionCarsCore3],
    ["Steering & Suspension Systems", reportData?.selectedConditionCarsCore4],
    ["Fuel Systems", reportData?.selectedConditionCarsCore5],
    ["Battery Systems", reportData?.selectedConditionCarsCore6],
    ["Exhaust Systems", reportData?.selectedConditionCarsCore7],
    ["Filters", reportData?.selectedConditionCarsCore8],
    ["Ignition Systems", reportData?.selectedConditionCarsCore9],
    ["Lighting Systems", reportData?.selectedConditionCarsCore10],
    ["Clutch & Transmission", reportData?.selectedConditionCarsCore11],
    ["Sensors", reportData?.selectedConditionCarsCore12],
  ];

  const systemObsZones = [
    ["Engine Crank", !reportData?.isToggledEngineCrack],
    ["Instrument Cluster", !reportData?.isToggledWarning],
    ["Vibration, Noise, Smell", !reportData?.isToggledSuspicious],
    ["Excessive Smoke From Exhaust", !reportData?.isToggledSmoke],
    ["Gear Shifting", !reportData?.isToggledGearshift],
    ["Pedal Play", !reportData?.isToggledBreak],
    ["Steering Responsiveness", !reportData?.isToggledSteering],
    ["Parking Brake", !reportData?.isToggledHandbreak],
    ["Reverse Gear & Sensor", !reportData?.isToggledReverse],
    ["Speedometer & Tachometer", !reportData?.isToggledSmooth],
  ];

  const fluidZones = [
    ["Coolant", !reportData?.isToggledFluidLevel1 || reportData?.isToggledFluidLevel2],
    ["Engine Oil", !reportData?.isToggledFluidLevel3 || reportData?.isToggledFluidLevel4],
    ["Brake Oil", !reportData?.isToggledFluidLevel5 || reportData?.isToggledFluidLevel6],
    ["Washer Fluid", !reportData?.isToggledFluidLevel7 || reportData?.isToggledFluidLevel8],
    ["Auxiliary Belts", reportData?.isToggledFluidLevel9],
    ["DEF/DPF", reportData?.isToggledFluidLevel10 && reportData?.percentageValue < 50],
    ["Leakages", reportData?.isToggledFluidLevel11],
  ];

  const carpetDetail = (notPresent, condition) => {
    if (notPresent && hasContent(condition)) return `Not Present. ${displayValue(condition)}`;
    if (notPresent) return 'Not Present';
    return displayValue(condition) || '';
  };
  const carpetZones = [
    ["Driver Side", carpetDetail(reportData?.selectedButtonDS, reportData?.selectedConditionDS), "carpetFile1"],
    ["Co-Driver Side", carpetDetail(reportData?.selectedButtonCDS, reportData?.selectedConditionCDS), "carpetFile2"],
    ["Driver Rear Side", carpetDetail(reportData?.selectedButtonDRS, reportData?.selectedConditionDRS), "carpetFile3"],
    ["Co-Driver Rear Side", carpetDetail(reportData?.selectedButtonCDRS, reportData?.selectedConditionCDRS), "carpetFile4"],
    ["Boot", carpetDetail(reportData?.selectedButtonBI, reportData?.selectedConditionBI), "carpetFile5"],
    ["Roofing", carpetDetail(reportData?.selectedButtonRI, reportData?.selectedConditionRI), "carpetFile6"],
  ];

  const plasticZones = [
    ["Driver Side", reportData?.selectedConditionPDS, "plasticPanelFile1"],
    ["Co-Driver Side", reportData?.selectedConditionPCDS, "plasticPanelFile2"],
    ["Driver Rear Side", reportData?.selectedConditionPDRS, "plasticPanelFile3"],
    ["Co-Driver Rear Side", reportData?.selectedConditionPCDRS, "plasticPanelFile4"],
    ["Boot", reportData?.selectedConditionBIS, "plasticPanelFile5"],
    ["Dashboard", reportData?.selectedConditionD, "plasticPanelFile6"],
    ["Gear Console", reportData?.selectedConditionGC, "plasticPanelFile7"],
    ["AC Vent", reportData?.selectedConditionAC, "plasticPanelFile8"],
    ["Door Trim", reportData?.selectedConditionDT, "plasticPanelFile9"],
  ];

  const seatZones = [
    ["Driver Side", reportData?.selectedConditionSDS, "seatFile1"],
    ["Co-Driver Side", reportData?.selectedConditionSCDS, "seatFile2"],
    ["Driver Rear Side", reportData?.selectedConditionSDRS, "seatFile3"],
    ["Co-Driver Rear Side", reportData?.selectedConditionSCDRS, "seatFile4"],
    ["Driver Headrest", reportData?.selectedConditionHR, "seatFile5"],
    ["Co-Driver Headrest", reportData?.selectedConditionCHR, "seatFile6"],
    ["Rear Left Headrest", reportData?.selectedConditionRHR, "seatFile7"],
    ["Rear Right Headrest", reportData?.selectedConditionHCDR, "seatFile8"],
  ];

  const rubberZones = [
    ["Beading: Front Left Door", reportData?.selectedConditionRC1, "rubberComponentFile1"],
    ["Beading: Rear Left Door", reportData?.selectedConditionRC2, "rubberComponentFile2"],
    ["Beading: Boot", reportData?.selectedConditionRC3, "rubberComponentFile3"],
    ["Beading: Rear Right Door", reportData?.selectedConditionRC4, "rubberComponentFile4"],
    ["Beading: Front Right Door", reportData?.selectedConditionRC5, "rubberComponentFile5"],
    ["Front Under The Bonnet", reportData?.selectedConditionRC6, "rubberComponentFile6"],
  ];

  const glassLabel = (name, brand) => (brand ? `${name} (${brand})` : name);
  const glassZones = [
    [glassLabel("Front Windscreen", reportData?.selectedConditionGM1), reportData?.isToggledFW && reportData?.selectedConditionGM2, "glassMirrorFile1"],
    ["Front Wiper Left", reportData?.isToggledFWL && reportData?.selectedConditionGM3, "glassMirrorFile2"],
    ["Front Wiper Right", reportData?.isToggledFWR && reportData?.selectedConditionGM4, "glassMirrorFile3"],
    [glassLabel("Front Left ORVM Glass", reportData?.selectedConditionGM5), reportData?.isToggledL && reportData?.selectedConditionGM6, "glassMirrorFile4"],
    [glassLabel("Front Right Door Window", reportData?.selectedConditionGM7), reportData?.isToggledFRDGW && reportData?.selectedConditionGM8, "glassMirrorFile5"],
    [glassLabel("Front Right ORVM Glass", reportData?.selectedConditionGM9), reportData?.isToggledFRORVMG && reportData?.selectedConditionGM10, "glassMirrorFile6"],
    [glassLabel("Rear Right Door Window", reportData?.selectedConditionGM11), reportData?.isToggledRRDW && reportData?.selectedConditionGM12, "glassMirrorFile7"],
    [glassLabel("Rear Windscreen", reportData?.selectedConditionGM13), reportData?.isToggledRWGD && reportData?.selectedConditionGM14, "glassMirrorFile8"],
    ["Rear Wiper", reportData?.isToggledRW && reportData?.selectedConditionGM15, "glassMirrorFile9"],
    [glassLabel("Rear Left Door Window", reportData?.selectedConditionGM16), reportData?.isToggledRLDWN && reportData?.selectedConditionGM17, "glassMirrorFile10"],
    [glassLabel("Front Left Door Window", reportData?.selectedConditionGM18), reportData?.isToggledFLDWN && reportData?.selectedConditionGM19, "glassMirrorFile11"],
    [glassLabel("Sunroof", reportData?.selectedConditionGM20), reportData?.isToggledSR && reportData?.selectedConditionGM21, "glassMirrorFile12"],
  ];

  const infotainmentZones = [
    ["Infotainment System", reportData?.isToggledIS && reportData?.selectedConditionIS],
    ["Speaker & Sound Quality", reportData?.isToggledSASQ && reportData?.selectedConditionSASQ],
    ["Power Windows", reportData?.isToggledPW && reportData?.selectedConditionPW],
    ["Gesture Controls", reportData?.isToggledGC && reportData?.selectedConditionGCO],
    ["Camera Quality", reportData?.isToggledCQ && reportData?.selectedConditionCQ],
    ["Instrument Cluster", reportData?.isToggledIC && reportData?.selectedConditionIC],
    ["Air Bags", reportData?.isToggledAB && reportData?.selectedConditionAB],
    ["Steering Controls", reportData?.isToggledSC && reportData?.selectedConditionSC],
    ["Interior Lighting", reportData?.isToggledIL && reportData?.selectedConditionIL],
  ];

  const tyreZones = [
    ["Front Right", reportData?.BrandT, reportData?.BrandTv, reportData?.selectedConditionSTS, reportData?.selectedConditionFT, reportData?.selectedConditionDOTFT, reportData?.BrandTRFR, "tyreFile1"],
    ["Rear Right", reportData?.BrandRT, reportData?.VarientRT, reportData?.selectedConditionRT, reportData?.selectedConditionRTN, reportData?.selectedConditionDOTRT, reportData?.TDRT, "tyreFile2"],
    ["Rear Left", reportData?.BrandRL, reportData?.VarientRL, reportData?.selectedConditionRL, reportData?.selectedConditionRLN, reportData?.selectedConditionDOTRL, reportData?.RLTrend, "tyreFile3"],
    ["Front Left", reportData?.BrandFLN, reportData?.VarientFLN, reportData?.selectedConditionFLN, reportData?.selectedConditionFLNT, reportData?.selectedConditionDOTFLN, reportData?.FLN, "tyreFile4"],
    ["Spare", reportData?.BrandSpare, reportData?.VarientSpare, reportData?.selectedConditionSpare, reportData?.selectedConditionSparen, reportData?.selectedConditionDOTSpare, reportData?.Spare, "tyreFile5"],
  ];

  // Technician's own 0-10 judgment rating, kept separate from the
  // deduction-based score (confirmed scope decision — no weighting math).
  const ratingZones = [
    ["Exterior Condition", reportData?.rating1],
    ["Interior Condition", reportData?.rating2],
    ["Engine Health", reportData?.rating3],
    ["Transmission and Clutch Health", reportData?.rating4],
    ["Lights Systems", reportData?.rating5],
    ["Infotainment System", reportData?.rating6],
    ["4x4 / AWD (if any)", reportData?.rating7],
    ["Tyres and Rims Condition", reportData?.rating8],
    ["Test Drive Rating", reportData?.rating9],
    ["Overall Rating of Car", reportData?.rating10],
  ];

  // ---- Highlights (auto-generated from the same real flags above) ----

  const allChecks = [
    ...bodyPaintZones.map(([label, val]) => ({ area: label, group: 'Body & Paint', flagged: hasContent(val), detail: displayValue(val) })),
    ...panelGapZones.map(([label, gap, rough]) => ({ area: `${label} Panel Gap`, group: 'Body & Paint', flagged: !!(gap || rough) })),
    ...coreMechZones.map(([label, val]) => ({ area: label, group: 'Core Mechanical', flagged: hasContent(val), detail: displayValue(val) })),
    ...systemObsZones.map(([label, flagged]) => ({ area: label, group: 'System Observations', flagged: !!flagged })),
    ...fluidZones.map(([label, flagged]) => ({ area: label, group: 'Fluids', flagged: !!flagged })),
    ...tyreZones.map(([label, , , , val]) => ({ area: `${label} Tyre`, group: 'Tyres', flagged: hasContent(val), detail: displayValue(val) })),
    ...carpetZones.map(([label, val]) => ({ area: `Carpet — ${label}`, group: 'Interior', flagged: !!val, detail: val })),
    ...plasticZones.map(([label, val]) => ({ area: `Plastic — ${label}`, group: 'Interior', flagged: hasContent(val), detail: displayValue(val) })),
    ...seatZones.map(([label, val]) => ({ area: `Seat — ${label}`, group: 'Interior', flagged: hasContent(val), detail: displayValue(val) })),
    ...rubberZones.map(([label, val]) => ({ area: label, group: 'Interior', flagged: hasContent(val), detail: displayValue(val) })),
    ...glassZones.map(([label, val]) => ({ area: label, group: 'Interior', flagged: hasContent(val), detail: displayValue(val) })),
    ...infotainmentZones.map(([label, val]) => ({ area: label, group: 'Interior', flagged: hasContent(val), detail: displayValue(val) })),
  ];
  const needsAttention = allChecks.filter((c) => c.flagged);
  const workingWell = allChecks.filter((c) => !c.flagged);
  const workingWellSample = workingWell.slice(0, 5);
  const needsAttentionSample = needsAttention.slice(0, 5);

  const socialIcons = [
    { src: facebook, href: 'https://www.facebook.com/share/1AhBm5rC7f/' },
    { src: twitter, href: 'https://x.com/i/flow/login?redirect_after_login=%2FCarvetPro' },
    { src: instagram, href: 'https://www.instagram.com/carvetproindia?igsh=MWVraHVydzUydDc5aA==' },
    { src: youtube, href: 'https://youtube.com/@carvetproindia?si=LvAdbNabFiNvC8Pc' },
  ];
  const footerServices = [
    ['Pre Delivery Inspection', 'https://carvetpro.com/service?inspectionType=Pre%20Delivery%20Inspection'],
    ['Used Car Inspection', 'https://carvetpro.com/service?inspectionType=Used%20Car%20Inspections'],
    ['Pre-Trip Inspection', 'https://carvetpro.com/service?inspectionType=Pre%20Trip%20Inspections'],
    ['Driver Assistance', null],
  ];
  const currentYear = new Date().getFullYear();

  // Body & Paint diagram — same decorative tyre.png overlay as the PDI
  // report (confirmed: not tied to zone count, kept as-is for this larger
  // 26-zone list too).
  // wrap={false}: the base image and its SVG leader-lines + labels are one
  // visual unit — if the container split at a page boundary the lines would
  // point at nothing and the labels would sit apart from the image. Forcing
  // the whole 523x230 composite to move together prevents that.
  const CarDiagram = () => (
    <View wrap={false} style={{ width: 523, height: 230, position: 'relative' }}>
      <Image src={tyreDiagramImg} style={{ position: 'absolute', left: 90, top: 40, width: 340, height: 130 }} />
      <Svg width="523" height="230" viewBox="0 0 523 230" style={{ position: 'absolute', left: 0, top: 0 }}>
        <Line x1="130" y1="103" x2="60" y2="100" stroke={LIME} strokeWidth={1} />
        <Text x="10" y="105" style={{ fontSize: 8, fontFamily: 'PoppinsS' }}>Front Wing</Text>

        <Line x1="176" y1="57" x2="150" y2="20" stroke={LIME} strokeWidth={1} />
        <Text x="105" y="15" style={{ fontSize: 8, fontFamily: 'PoppinsS' }}>Front Wheel — Top</Text>

        <Line x1="176" y1="144" x2="140" y2="185" stroke={LIME} strokeWidth={1} />
        <Text x="10" y="197" style={{ fontSize: 8, fontFamily: 'PoppinsS' }}>Front Wheel — Bottom</Text>

        <Line x1="191" y1="103" x2="180" y2="185" stroke={LIME} strokeWidth={1} />
        <Text x="170" y="197" style={{ fontSize: 8, fontFamily: 'PoppinsS' }}>Nose Cone</Text>

        <Line x1="238" y1="102" x2="255" y2="15" stroke={LIME} strokeWidth={1} />
        <Text x="212" y="10" style={{ fontSize: 8, fontFamily: 'PoppinsS' }}>Cockpit / Halo</Text>

        <Line x1="360" y1="57" x2="390" y2="20" stroke={LIME} strokeWidth={1} />
        <Text x="345" y="15" style={{ fontSize: 8, fontFamily: 'PoppinsS' }}>Rear Wheel — Top</Text>

        <Line x1="360" y1="153" x2="390" y2="185" stroke={LIME} strokeWidth={1} />
        <Text x="335" y="197" style={{ fontSize: 8, fontFamily: 'PoppinsS' }}>Rear Wheel — Bottom</Text>

        <Line x1="397" y1="103" x2="460" y2="100" stroke={LIME} strokeWidth={1} />
        <Text x="410" y="105" style={{ fontSize: 8, fontFamily: 'PoppinsS' }}>Rear Wing</Text>
      </Svg>
    </View>
  );

  return (
  <Document>

    {/* ================= PAGE 1 — COVER ================= */}
    <Page size="A4" style={styles.darkPage}>
      <View style={{ paddingHorizontal: 40, paddingTop: 50 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 40 }}>
          <Image style={{ width: 130, height: 38 }} src={logo} />
        </View>
        <Text style={{ fontFamily: 'Clash', fontSize: 40, color: '#FFFFFF', marginBottom: 8 }}>
          Used Car Inspection Report
        </Text>
        <Text style={{ fontSize: 11, color: LIME_TINT, marginBottom: 24 }}>
          A 350-point evaluation before you buy, so surprises stay off the road.
        </Text>

        <View style={{ flexDirection: 'row', flexWrap: 'wrap', marginBottom: 30 }}>
          {[
            'Experienced Evaluators',
            'Comprehensive Photo Documentation',
            'Deduction-Based Scoring',
            'Transparent, Field-by-Field Reporting',
          ].map((t) => (
            <View key={t} style={{
              backgroundColor: 'rgba(173,209,68,0.12)', borderWidth: 1, borderColor: LIME,
              borderRadius: 20, paddingHorizontal: 14, paddingVertical: 8, marginRight: 10, marginBottom: 10,
            }}>
              <Text style={{ fontSize: 9, color: LIME_TINT, fontFamily: 'PoppinsS' }}>{t}</Text>
            </View>
          ))}
        </View>

        <View style={{ flexDirection: 'row', marginBottom: 10 }}>
          <Text style={{ fontSize: 10, color: '#FFFFFF' }}>
            {serviceData?.address ? serviceData.address.split(',').slice(-1)[0].trim() : 'Bangalore'}
          </Text>
          <Text style={{ fontSize: 10, color: LIME, marginHorizontal: 8 }}>|</Text>
          <Text style={{ fontSize: 10, color: '#FFFFFF' }}>
            {reportData?.pdiDate ? reportData.pdiDate : new Date().toLocaleDateString('en-IN')}
          </Text>
        </View>
      </View>

      <View style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 460 }}>
        <Image src={carImageUrl || car} style={{ width: '100%', height: 460, objectFit: 'cover', objectPosition: 'bottom', opacity: 0.85 }} />
        <Svg width="595" height="460" style={{ position: 'absolute', top: 0, left: 0 }}>
          <Defs>
            <LinearGradient id="coverFade" x1="0" y1="0" x2="0" y2="1">
              <Stop offset="0" stopColor={DARK} stopOpacity="1" />
              <Stop offset="0.25" stopColor={DARK} stopOpacity="0.1" />
              <Stop offset="1" stopColor={DARK} stopOpacity="0.4" />
            </LinearGradient>
          </Defs>
          <Rect x="0" y="0" width="595" height="460" fill="url(#coverFade)" />
        </Svg>
      </View>
    </Page>

    {/* ================= PAGE 2 — OVERVIEW =================
        Used cars have no PDI-issued VIN check — identity is registration
        number + chassis number instead, plus ownership history and a
        chassis-condition issue list (Condition Of Chassis Number), all
        traced from the pre-redesign template's Overview card. */}
    <Page size="A4" style={styles.page}>
      <PageHeader />
      <View style={styles.body}>
        <Text style={styles.h1}>Overview</Text>
        <Text style={{ fontSize: 10, color: TEXT_LABEL, marginBottom: 12 }}>
          Prepared for {serviceData?.customerName || 'N/A'}
        </Text>

        <View wrap={false} style={[styles.row, { marginBottom: 16 }]}>
          <Image style={{ width: 150, height: 110, borderRadius: 10, marginRight: 16 }} src={carImageUrl || car} />
          <View style={{ flex: 1 }}>
            <Text style={{ fontFamily: 'Clash', fontSize: 18, color: DARK }}>
              {serviceData?.vehicleMake && serviceData?.vehicleModel ? `${serviceData.vehicleMake} ${serviceData.vehicleModel}` : 'N/A'}
            </Text>
            <Text style={{ fontSize: 10, color: TEXT_LABEL, marginTop: 2, marginBottom: 10 }}>
              {serviceData?.variant || 'N/A'}{reportData?.transmission ? ` | ${reportData.transmission}` : ''}
            </Text>
            <View style={styles.divider} />
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text style={{ fontSize: 10, color: TEXT_LABEL }}>Overall Reliability Score</Text>
              <View style={[styles.badge, { backgroundColor: tierFromScore(Number(scorePercentage)).bg }]}>
                <Text style={{ fontSize: 13, fontFamily: 'PoppinsS', color: tierFromScore(Number(scorePercentage)).color }}>
                  {scorePercentage}% · {tierLabelTitleCase(Number(scorePercentage))}
                </Text>
              </View>
            </View>
            <Text style={{ fontSize: 8, color: TEXT_LABEL, marginTop: 6 }}>
              {(() => {
                const s = Number(scorePercentage);
                if (s >= 90) return 'This score signifies that this car is free of defects and meets the highest standards.';
                if (s >= 75) return 'This score indicates the car is in good condition with only minor defects.';
                if (s >= 60) return 'This score indicates the car has moderate defects but is generally functional.';
                return 'This score reflects significant defects, requiring immediate attention or repairs.';
              })()}
            </Text>
          </View>
        </View>

        <View style={[styles.cardGray, { flexDirection: 'row' }]}>
          <View style={{ flex: 1 }}>
            <Text style={styles.label}>BHP</Text>
            <Text style={styles.value}>{serviceData?.bhp || 'N/A'}</Text>
          </View>
          <View style={{ flex: 1 }}>
            <Text style={styles.label}>Airbags</Text>
            <Text style={styles.value}>{serviceData?.airbags || 'N/A'}</Text>
          </View>
          <View style={{ flex: 1 }}>
            <Text style={styles.label}>Mileage (kmpl)</Text>
            <Text style={styles.value}>{serviceData?.mileage || 'N/A'}</Text>
          </View>
          <View style={{ flex: 1 }}>
            <Text style={styles.label}>NCAP</Text>
            <Text style={styles.value}>{serviceData?.ncap || 'N/A'}</Text>
          </View>
        </View>

        <View style={styles.card}>
          <Text style={[styles.h2, { fontFamily: 'PoppinsS', fontSize: 17, color: '#000000' }]}>Vehicle &amp; Booking Details</Text>
          <View style={styles.grid2}>
            {[
              ['Booking ID', shortBookingId(serviceData?._id)],
              ['Service Type', serviceData?.inspectionType],
              ['Customer Phone', serviceData?.customerPhone],
              ['PDI Date', reportData?.pdiDate || new Date().toLocaleDateString('en-IN')],
              ['PDI Time', reportData?.pdiTime || new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })],
              ['Conducted By', reportData?.technicianName],
              ['Registration Number', reportData?.registrationNumber],
              ['Chassis Number', reportData?.chaseNumber],
              ['Number of Owners', reportData?.ownership],
              ['Engine Number', reportData?.engineNumber],
              ['Engine Type', serviceData?.engine],
              ['Fuel', serviceData?.fuel],
              ['Emission Standard', reportData?.emissionType],
              ['Transmission', reportData?.transmission],
              ['Keys', reportData?.keysBasic],
              ['Dealer Type', serviceData?.dealerType],
              ['Vehicle Type', serviceData?.vehicleType],
              ['Delivery Date', serviceData?.deliveryDate ? new Date(serviceData.deliveryDate).toLocaleDateString('en-IN') : null],
              ['Dealer Showroom', reportData?.selectedButton ? 'Done' : 'N/A'],
            ].map(([l, v]) => (
              <View style={styles.gridCell} key={l}>
                <Text style={styles.label}>{l}</Text>
                <Text style={styles.value}>{v || 'N/A'}</Text>
              </View>
            ))}
          </View>

          <View style={{ marginTop: 4 }}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <Text style={{ fontFamily: 'PoppinsS', fontSize: 10, color: DARK }}>Condition Of Chassis Number</Text>
              <Badge tier={reportData?.isToggledCC && hasContent(reportData?.selectedConditionCC) ? STATUS.attention : STATUS.excellent} />
            </View>
            {reportData?.isToggledCC && hasContent(reportData?.selectedConditionCC) ? (
              <View style={styles.reasonBox}>
                <Text style={styles.reasonLine}>
                  <Text style={styles.reasonLabel}>Reason: </Text>
                  <Text style={styles.reasonText}>{displayValue(reportData.selectedConditionCC)}</Text>
                </Text>
              </View>
            ) : (
              <Text style={{ fontSize: 9, color: TEXT_BODY }}>No chassis-number irregularities noted.</Text>
            )}
          </View>

          <View style={{ marginTop: 10 }}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <Text style={{ fontFamily: 'PoppinsS', fontSize: 10, color: DARK }}>Car Driven Before PDI Date</Text>
              <Badge tier={isOdometerConditionMet === 'Yes' ? STATUS.attention : STATUS.excellent} />
            </View>
            {isOdometerConditionMet === 'Yes' ? (
              <View style={styles.reasonBox}>
                <Text style={styles.reasonLine}>
                  <Text style={styles.reasonLabel}>Reason: </Text>
                  <Text style={styles.reasonText}>
                    {`Current ODO: ${reportData?.odometerBasic ?? 'N/A'} km · Recorded vehicle usage: ${reportData?.distKMS ?? 'N/A'} km. The odometer reading at inspection is lower than recorded usage — possible tampering or a data-entry mismatch, worth confirming with the seller.`}
                  </Text>
                </Text>
              </View>
            ) : (
              <Text style={{ fontSize: 9, color: TEXT_BODY }}>
                {`Current ODO: ${reportData?.odometerBasic ?? 'N/A'} km · Recorded vehicle usage: ${reportData?.distKMS ?? 'N/A'} km. No discrepancy found.`}
              </Text>
            )}
          </View>
        </View>

        {/* Overview, Highlights, and Score Summary/Gallery all share the
            same default header ("Used Car Inspection Report") so merging
            them into one continuous Page costs nothing label-wise — Rule 1
            applied from the start on this template, not retrofitted. */}
        <View style={{ marginTop: 20 }}>
        <Text style={styles.h1}>What Needs Attention</Text>
        <Text style={styles.subtitle}>Areas flagged by the technician that may require repair or servicing</Text>
        {needsAttentionSample.length === 0 && <Text style={{ fontSize: 9, color: TEXT_LABEL }}>No issues were flagged during this inspection.</Text>}
        {needsAttentionSample.map((c, i) => (
          <View key={i} wrap={false} style={[styles.highlightRow, { backgroundColor: '#FFF3E8' }]}>
            <View style={[styles.highlightDot, { backgroundColor: '#D97706' }]} />
            <Text style={styles.highlightText}>{c.area}{c.detail ? ` — ${c.detail}` : ' has an issue flagged.'}</Text>
          </View>
        ))}
        {needsAttention.length > needsAttentionSample.length && (
          <Text style={{ fontSize: 8, color: TEXT_LABEL, marginTop: 4 }}>
            +{needsAttention.length - needsAttentionSample.length} more flagged item(s) — see the detailed sections that follow.
          </Text>
        )}

        <TechnicianRemarks reportData={reportData} />
        </View>

        <View style={{ marginTop: 20 }}>
        <Text style={styles.h1}>Inspection Overview</Text>
        <Text style={styles.subtitle}>Category scores, derived from the same deduction totals used for the overall reliability score</Text>

        {[
          ['Body & Paint', scoreBodyPaint, bodyPaintZones.filter(z => hasContent(z[1])).length, bodyPaintZones.length + panelGapZones.length],
          ['Engine, Transmission & System Observations', scoreEngineTrans, systemObsZones.filter(z => z[1]).length + (enginePerformance.some(Boolean) ? 1 : 0), systemObsZones.length + enginePerformance.length],
          ['Steering, Suspension, Brakes & Core Mechanical', scoreMechFluids, coreMechZones.filter(z => z[1]).length + fluidZones.filter(z => z[1]).length, coreMechZones.length + fluidZones.length],
          ['Tyres', scoreTyres, tyreZones.filter(z => z[4]).length, tyreZones.length],
          ['Interior, Comfort & Electronics', scoreInterior,
            [...carpetZones, ...plasticZones, ...seatZones, ...rubberZones, ...glassZones].filter(z => hasContent(z[1])).length + infotainmentZones.filter(z => hasContent(z[1])).length,
            carpetZones.length + plasticZones.length + seatZones.length + rubberZones.length + glassZones.length + infotainmentZones.length],
        ].map(([name, score, flaggedCount, total]) => {
          const tier = tierFromScore(score);
          return (
            <View style={styles.scoreCard} key={name} wrap={false}>
              <View style={styles.scoreCardHeadRow}>
                <Text style={styles.scoreCardTitle}>{name}</Text>
                <Badge tier={tier} />
              </View>
              <Text style={styles.scoreCardDesc}>
                {flaggedCount === 0
                  ? 'All checks in this category are in good condition.'
                  : 'Some checks in this category are flagged for attention.'}
              </Text>
            </View>
          );
        })}

        {[reportData?.basicFile1, reportData?.basicFile2, reportData?.basicFile3, reportData?.basicFile4, reportData?.basicFile5, reportData?.paintBodyFile1].some(Boolean) && (
          <View style={{ marginTop: 20 }}>
            <Text style={styles.h1}>Vehicle Inspection Gallery</Text>
            <Text style={styles.subtitle}>Hero angles captured during inspection</Text>
            <View style={styles.photoGrid}>
              <PhotoCard src={reportData?.basicFile1} caption="Front View" wide />
              <PhotoCard src={reportData?.basicFile2} caption="Rear View" wide />
              <PhotoCard src={reportData?.basicFile3} caption="Side Profile" wide />
              <PhotoCard src={reportData?.basicFile4} caption="Side Profile" wide />
              <PhotoCard src={reportData?.basicFile5} caption="Additional Angle" wide />
              <PhotoCard src={reportData?.paintBodyFile1} caption="Engine Bay Context — Bonnet" wide />
            </View>
          </View>
        )}
        </View>

        {/* Body & Paint used to start a brand-new <Page> here. Whenever the
            Gallery above ended with a partial last row, that left a
            trailing photo (or two) alone on a physical page with the rest
            of it empty, because Body & Paint couldn't flow up into that
            leftover space from a separate Page component. Merged into this
            same Page so it can (same fix, same reasoning, as the PDI
            report's identical Gallery→Detailed-Findings gap). */}

        {/* ================= DETAILED INSPECTION FINDINGS =============
            Same Rule 1 / Rule 2 discipline established on the PDI report: every
            section flows into the next on one continuous Page, only breaking
            where content genuinely doesn't fit; ZoneRow itself (not the card
            or section) is the atomic wrap={false} unit, so a table can span a
            page boundary cleanly instead of the whole card jumping together. */}
        <Text style={styles.h1}>Body &amp; Paint Overview</Text>
        <Text style={styles.subtitle}>Zone-by-zone exterior condition, plus panel gap fit-and-finish checks</Text>

        <View wrap={false} style={[styles.cardGray, { alignItems: 'center', paddingVertical: 20 }]}>
          <CarDiagram />
        </View>

        <ZoneGrid zones={bodyPaintZones} />

        <View style={{ marginTop: 20 }}>
          <Text style={styles.h2}>Panel Gap Measurements</Text>
          <ZoneGrid useExplicitFlag zones={panelGapZones.map(([label, gapFlag, rough, grt, grd]) => {
            const flagged = !!(gapFlag || rough);
            return [label, `GRT: ${grt || '—'}  GRD: ${grd || '—'}${rough ? '  (Rough)' : ''}`, flagged];
          })} />
          <Text style={{ fontSize: 7.5, color: TEXT_LABEL, marginTop: 6 }}>GRT: Gap Reading Top · GRD: Gap Reading Down</Text>

          <Legend2 />
        </View>

        {bodyPaintZones.some(([, , fileKey]) => reportData?.[fileKey]) && (
          <View style={{ marginTop: 20 }}>
            <Text style={styles.h2}>Body &amp; Paint — Photo Documentation</Text>
            <View style={styles.photoGrid}>
              {bodyPaintZones.map(([label, , fileKey]) => (
                <PhotoCard key={fileKey} src={reportData?.[fileKey]} caption={label} />
              ))}
            </View>
          </View>
        )}

        <View style={{ marginTop: 20 }}>
        <Text style={styles.h1}>Engine &amp; Transmission</Text>
        <Text style={styles.subtitle}>Real-time parameter evaluation and driver-facing system observations</Text>

        <View style={styles.card}>
          <Text style={styles.h2}>Real-Time Parameter Evaluation (Idle)</Text>
          {[
            ['Battery Voltage (v)', reportData?.BatteryEnergy, reportData?.BatteryEnergy < 11.5],
            ['Engine Load (%)', reportData?.LoadEngine, reportData?.LoadEngine > 35],
            ['Idle RPM', reportData?.idleRPM, reportData?.idleRPM > 1400],
          ].map(([label, val, flagged]) => (
            <ZoneRow key={label} label={`${label}: ${val ?? 'N/A'}`} value={flagged ? 'Outside expected range' : ''} flagged={!!flagged} />
          ))}
          {Array.isArray(reportData?.errorCodes) && reportData.errorCodes.length > 0 && (
            <Text style={{ fontSize: 8.5, color: '#D97706', marginTop: 8 }}>
              OBD Error Codes / Sensor &amp; CanBus Faults: {reportData.errorCodes.join(', ')}
            </Text>
          )}
        </View>

        <View style={styles.card}>
          <Text style={styles.h2}>System Observations</Text>
          <ZoneGrid zones={systemObsZones} />
        </View>

        <Legend2 />
        </View>

        <View style={{ marginTop: 20 }}>
        <Text style={styles.h1}>Core Mechanical System Check</Text>
        <Text style={styles.subtitle}>Steering, suspension, brakes, and the vehicle's core mechanical systems</Text>

        {/* wrap={false}: this specific card kept landing right at a
            physical page boundary and rendering as garbled/overlapping
            text (a react-pdf Yoga bug, same class fixed on Pre-Trip's
            identical Core Mechanical card earlier this session) — caught
            here via this round's full re-verification pass, after the
            Gallery→Body&Paint merge shifted where page breaks fall
            throughout the document. */}
        <View style={styles.card} wrap={false}>
          <ZoneGrid zones={coreMechZones} />
        </View>

        <View style={styles.card}>
          <Text style={styles.h2}>Fluid Inspection</Text>
          <ZoneGrid zones={fluidZones} />
        </View>

        <Legend2 />

        <View style={{ marginTop: 20 }}>
          <Text style={styles.h1}>Tyres</Text>
          <Text style={styles.subtitle}>Detailed examination of tyre health, including tread depth and overall wear</Text>

          <View style={styles.photoGrid}>
            {tyreZones.map(([label, brand, variant, size, condition, dot, treadRaw, fileKey]) => {
              const pct = treadPercentNum(treadRaw);
              return (
                <View wrap={false} style={styles.photoCell3} key={label}>
                  {reportData?.[fileKey] && <Image style={styles.photoImg} src={reportData[fileKey]} />}
                  <Text style={styles.photoCaption}>{label} Tyre</Text>
                  <Text style={{ fontSize: 8, color: TEXT_LABEL, marginTop: 2 }}>
                    {pct !== null ? `${pct}% Tread Remaining` : (treadRaw ? `Raw reading: ${treadRaw}` : 'No reading recorded')}
                  </Text>
                </View>
              );
            })}
          </View>

          <View style={styles.card}>
            {tyreZones.map(([label, brand, variant, size, condition, dot]) => (
              <ZoneRow
                key={label}
                label={label}
                value={`Brand: ${brand || 'N/A'} · Variant: ${variant || 'N/A'} · Size: ${size || 'N/A'} · DOT/Mfg: ${dot || 'N/A'}`}
                reason={hasContent(condition) ? condition : ''}
                flagged={hasContent(condition)}
              />
            ))}
          </View>

          <View wrap={false} style={styles.footnote}>
            <Text style={styles.footnoteText}>
              Tread % is estimated against a 9mm new-tyre reference depth (consistent with CarvetPro's existing tread-depth convention); actual new-tyre depth varies by model. &gt;80% good life remaining, &lt;20% replacement needed.
            </Text>
          </View>
        </View>
        </View>

        <View style={{ marginTop: 20 }}>
        <Text style={styles.h1}>Interior, Comfort &amp; Electronics</Text>
        <Text style={styles.subtitle}>Carpet, plastic trim, seating, rubber seals, glass &amp; mirrors, and infotainment</Text>

        {[
          ['Flooring & Carpet Review', carpetZones],
          ['Plastic Component Inspection', plasticZones],
          ['Seat Condition & Comfort', seatZones],
          ['Rubber Parts Quality Assessment', rubberZones],
          ['Glass & Mirror Inspection', glassZones],
        ].map(([title, zones]) => (
          <View style={styles.card} key={title}>
            <Text style={styles.h2}>{title}</Text>
            {zones.map(([label, val]) => (
              <ZoneRow key={label} label={label} value={val} />
            ))}
            <PhotoStrip zones={zones} reportData={reportData} />
          </View>
        ))}

        {/* Infotainment card, then the section's trailing legend. Legend2's
            row is wrap={false} so a legend key can never split between its
            badge and caption (the round-2 fix). The card flows naturally
            after Glass & Mirror — no wrap={false} wrapper forcing the whole
            block to jump to the next page and leave a gap. */}
        <View style={styles.card}>
          <Text style={styles.h2}>Infotainment &amp; Electronic Features</Text>
          {infotainmentZones.map(([label, val]) => (
            <ZoneRow key={label} label={label} value={val} />
          ))}
        </View>

        <Legend2 />
        </View>

        {/* Technician Rating on Car + Flagged Issues flow continuously in
            this same Page — no forced <Page> — so they only break where
            content genuinely doesn't fit, and the legend above can never be
            marooned alone on an otherwise-blank page. Rendering every page
            of the heaviest (all-flagged) data confirms nothing after this
            point is dropped. Header-label tradeoff already accepted for the
            Gallery -> Body & Paint merge above.
            Technician Rating: a 0-10 technician-judgment scale, deliberately
            NOT a deduction-driven badge — visually distinct (stars) so a
            reader can't mistake it for another outcomes-based score. */}
        <View style={{ marginTop: 20 }}>
        <Text style={styles.h1}>Technician Rating on Car</Text>
        <Text style={styles.subtitle}>The inspecting technician's own judgment-based rating — separate from the deduction-based score above</Text>

        <View style={styles.card}>
          {ratingZones.map(([label, val]) => (
            <View key={label} style={styles.ratingRow}>
              <Text style={styles.ratingLabel}>{label}</Text>
              <StarRow value={val} />
            </View>
          ))}
        </View>
        </View>

        <View style={{ marginTop: 20 }}>
        <Text style={styles.h1}>Flagged Issues</Text>
        <Text style={styles.subtitle}>Photo reference — only zones with a technician-flagged issue and an available photo are shown here</Text>

        <View style={styles.photoGrid}>
          {[
            ...bodyPaintZones.map(([label, val, fileKey]) => ({ label, val, fileKey })),
            ...carpetZones.map(([label, val, fileKey]) => ({ label: `Carpet — ${label}`, val, fileKey })),
            ...plasticZones.map(([label, val, fileKey]) => ({ label: `Plastic — ${label}`, val, fileKey })),
            ...seatZones.map(([label, val, fileKey]) => ({ label: `Seat — ${label}`, val, fileKey })),
            ...rubberZones.map(([label, val, fileKey]) => ({ label, val, fileKey })),
            ...glassZones.map(([label, val, fileKey]) => ({ label, val, fileKey })),
            ...tyreZones.map(([label, , , , val, , , fileKey]) => ({ label: `${label} Tyre`, val, fileKey })),
          ].filter((z) => hasContent(z.val) && reportData?.[z.fileKey]).map((z) => (
            <PhotoCard key={z.fileKey} src={reportData[z.fileKey]} caption={`${z.label} — ${displayValue(z.val) || 'Issue flagged'}`} />
          ))}
        </View>
        {[
          ...bodyPaintZones, ...carpetZones, ...plasticZones, ...seatZones, ...rubberZones, ...glassZones,
        ].filter(([, val, fileKey]) => hasContent(val) && reportData?.[fileKey]).length === 0 && (
          <Text style={{ fontSize: 9, color: TEXT_LABEL, fontFamily: 'PoppinsS' }}>No flagged zones had an accompanying photo for this inspection.</Text>
        )}
        </View>
      </View>
    </Page>

    {/* ================= PAGE 12 — CLOSING ================= */}
    <Page size="A4" style={styles.darkPage}>
      <View style={{ flex: 1, flexDirection: 'column', justifyContent: 'space-between' }}>
        <View style={{ paddingHorizontal: 40, paddingTop: 70 }}>
          <Text style={{ fontFamily: 'Clash', fontSize: 40, color: '#FFFFFF', marginBottom: 8 }}>Thank You</Text>
          <Text style={{ fontSize: 13, color: LIME_TINT, marginBottom: 40 }}>for choosing CarvetPro</Text>

          <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
            {[
              ['Pre Delivery Inspection', 'Thorough pre-delivery checks so you take ownership with total confidence.', 'https://carvetpro.com/service?inspectionType=Pre%20Delivery%20Inspection'],
              ['Used Car Inspection', 'A 350-point evaluation before you buy, so surprises stay off the road.', 'https://carvetpro.com/service?inspectionType=Used%20Car%20Inspections'],
              ['Pre-Trip Inspection', 'Route-ready checks before every long drive, so breakdowns don’t catch you off guard.', 'https://carvetpro.com/service?inspectionType=Pre%20Trip%20Inspections'],
              ['Driver Assistance', 'On-call technical support to keep you moving, wherever the trip takes you.', 'https://carvetpro.com/contact?type=Driver%20Assistance'],
            ].map(([title, desc, href]) => (
              <Link key={title} src={href} style={{ width: '48%', marginRight: '2%', marginBottom: 20, backgroundColor: 'rgba(173,209,68,0.08)', borderRadius: 10, padding: 24, borderWidth: 1, borderColor: 'rgba(173,209,68,0.3)', textDecoration: 'none' }}>
                <Text style={{ fontFamily: 'PoppinsS', fontSize: 13, color: '#FFFFFF', marginBottom: 8 }}>{title}</Text>
                <Text style={{ fontSize: 9, color: '#D8D8D8', lineHeight: 1.4 }}>{desc}</Text>
              </Link>
            ))}
          </View>

          <Text style={{ fontSize: 9.5, color: '#9AA37A', marginTop: 10 }}>
            Questions about this report or your vehicle? Reach us anytime — details below.
          </Text>
        </View>

        <View style={{ paddingHorizontal: 40, flexDirection: 'row', flexWrap: 'wrap' }}>
          {[
            'Experienced Evaluators',
            'Comprehensive Photo Documentation',
            'Deduction-Based Scoring',
            'Transparent, Field-by-Field Reporting',
          ].map((t) => (
            <View key={t} style={{
              backgroundColor: 'rgba(173,209,68,0.12)', borderWidth: 1, borderColor: LIME,
              borderRadius: 20, paddingHorizontal: 14, paddingVertical: 8, marginRight: 10, marginBottom: 10,
            }}>
              <Text style={{ fontSize: 9, color: LIME_TINT, fontFamily: 'PoppinsS' }}>{t}</Text>
            </View>
          ))}
        </View>

        <View style={styles.footerContainer}>
          <View style={styles.contentWrapper}>
            <View style={styles.sectionss}>
              <Image src={footerLogo} style={styles.footerLogo} />
              <Text style={styles.description}>Your trusted partner for expert vehicle inspections and road-ready confidence.</Text>
              <View style={styles.socialIcons}>
                {socialIcons.map((icon, index) => (
                  <Link key={index} src={icon.href} style={styles.iconWrapper}>
                    <Image src={icon.src} style={styles.socialIcon} />
                  </Link>
                ))}
              </View>
            </View>
            <View style={styles.sectionss}>
              <Text style={styles.sectionHeading}>Our Services</Text>
              {footerServices.map(([label, href]) => (
                href
                  ? <Link key={label} src={href} style={[styles.listItem, { color: '#EDEDED', textDecoration: 'none' }]}>{label}</Link>
                  : <Text key={label} style={styles.listItem}>{label}</Text>
              ))}
            </View>
            <View style={styles.sectionss}>
              <Text style={styles.sectionHeading}>Contact Us</Text>
              <Link src="mailto:info@carvetpro.com" style={[styles.listItem, { color: '#EDEDED', textDecoration: 'none' }]}>info@carvetpro.com</Link>
              <Text style={styles.listItem}>+91 96115 60076</Text>
              <Text style={styles.listItem}>Bangalore, India 560100</Text>
            </View>
          </View>
          <View style={styles.dividers} />
          <Text style={styles.copyright}>CARVETPRO © All rights reserved. Copyright {currentYear}</Text>
        </View>
      </View>
    </Page>

    {/* ================= PAGE 13 — DISCLAIMER / T&C ================= */}
    <Page size="A4" style={styles.darkPage} wrap>
      <View style={{ paddingHorizontal: 36, paddingTop: 28 }}>
        <Text style={{ fontFamily: 'PoppinsS', fontSize: 14, color: '#FFFFFF', marginBottom: 8 }}>Disclaimer</Text>
        <Text style={{ fontSize: 7.5, color: '#CFCFCF', lineHeight: 1.5, marginBottom: 12 }}>
          This Inspection Report reflects the condition of the vehicle as observed and recorded by CarvetPro's evaluator at the time of inspection, to the best of their ability, using visual and functional checks and — where stated in this Report — basic diagnostic-tool readings (e.g. OBD parameters, tyre tread gauge). It does not involve dismantling components, paint-depth/thickness gauging, or laboratory-grade diagnostics unless explicitly stated. Vehicle condition can change after the inspection due to usage, weather, repairs, tampering, or defects that were not visible or detectable at the time of inspection. This Report is provided for informational purposes only and is not a warranty, certification, valuation, or guarantee of the vehicle's performance, condition, marketability, or fitness for any particular purpose. By relying on this Report, the customer agrees that CarvetPro (operated by the entity providing services at carvetpro.com), its employees, technicians, and affiliates are not liable for direct, indirect, incidental, or consequential claims, losses, depreciation, damages, or liabilities arising from the use, interpretation, or reliance on this Report, except as required by applicable Indian law.
        </Text>

        <Text style={{ fontFamily: 'PoppinsS', fontSize: 14, color: '#FFFFFF', marginBottom: 10 }}>Terms &amp; Conditions for Inspection Report</Text>
        {[
          ['1. Services Covered', 'CarvetPro provides vehicle inspection and related services: Pre-Delivery Inspection (PDI) of new vehicles before handover, Used Car Inspection prior to purchase, Pre-Trip Inspection before long journeys, and Driver Assistance support. This Report documents the findings of the specific service type stated on its cover page, performed on the date and at the odometer reading recorded therein.'],
          ['2. Nature of Assessment', 'The inspection is a point-in-time visual and functional assessment performed by a CarvetPro-affiliated technician, following an internal checklist covering exterior/structure, engine and transmission, steering/suspension/brakes, tyres, and interior/electronics. It does not involve dismantling parts or invasive testing unless explicitly stated, and cannot reveal hidden, latent, intermittent, or internal defects not apparent through visual/functional checks. Photographs included in this Report are illustrative of the technician\'s findings and are not an exhaustive photographic record of every component.'],
          ['3. No Warranty, Certification, or Valuation', 'This Report is not a certification, endorsement, warranty, or guarantee of the vehicle\'s condition, safety, performance, or future reliability, and is not a valuation or price recommendation. No statement in this Report, written or implied, constitutes a representation or warranty by CarvetPro regarding the vehicle inspected.'],
          ['4. Customer Reliance and Independent Verification', 'Reliance on this Report is at the customer\'s discretion. CarvetPro recommends that customers making a purchase decision based on this Report also seek independent verification — including the seller\'s own documentation, an independent mechanical inspection, or a diagnostic scan — particularly for defects noted as needing attention.'],
          ['5. Limitation of Liability', 'To the maximum extent permitted under Indian law, CarvetPro, its officers, employees, technicians, and affiliates are not liable for defects, mechanical or electronic failures, depreciation, accidents, or losses arising after the inspection date, or for conditions not reasonably detectable through the visual/functional assessment described in Section 2. This limitation does not exclude liability that cannot lawfully be excluded, including liability for fraud or gross negligence.'],
          ['6. Customer Disclosure Obligations', 'The customer (or the party requesting the inspection) is responsible for disclosing known issues, prior accidents, insurance claims, or modifications relevant to the vehicle before the inspection. CarvetPro is not responsible for omissions or misrepresentations made by the customer, the seller, or any third party involved in the transaction.'],
          ['7. Data Collection, Photographs & Privacy', 'In the course of this inspection, CarvetPro collected vehicle information (registration, chassis/engine numbers, odometer reading), inspection photographs, and the contact details of the person who booked the service, storing them securely (including via encrypted cloud storage) to generate and deliver this Report and to maintain service records. This data is processed in accordance with India\'s Digital Personal Data Protection Act, 2023, is not sold to third parties, and is retained only as long as reasonably needed for service delivery, support, and legal/record-keeping purposes. Questions or grievances regarding personal data in this Report can be directed to CarvetPro at info@carvetpro.com.'],
          ['8. Intellectual Property', 'This Report, including its layout, scoring methodology, and photographs, is proprietary to CarvetPro and is provided for the customer\'s personal reference in connection with the inspected vehicle. It may not be reproduced, resold, or represented as an independent third-party certification without CarvetPro\'s written consent.'],
          ['9. Governing Law & Dispute Resolution', 'These Terms are governed by the laws of India. Any dispute arising from this Report or the underlying inspection service shall first be raised with CarvetPro directly at info@carvetpro.com for resolution; unresolved disputes shall be subject to the exclusive jurisdiction of the courts of Bengaluru, Karnataka.'],
          ['10. Changes to These Terms', 'CarvetPro may update these Terms from time to time for future inspections; the version applicable to this Report is the one in effect on the inspection date stated on its cover page.'],
          ['11. Acceptance', 'Booking, receiving, downloading, or relying on this Report constitutes acceptance of these Terms & Conditions.'],
        ].map(([h, b], i, arr) => (
          <View key={h} style={{ marginBottom: 8 }} wrap={i < arr.length - 1 ? false : undefined}>
            <Text style={{ fontSize: 8.5, fontFamily: 'PoppinsS', color: LIME_TINT, marginBottom: 2 }}>{h}</Text>
            <Text style={{ fontSize: 7.5, color: '#CFCFCF', lineHeight: 1.4 }}>{b}</Text>
          </View>
        ))}
      </View>
    </Page>

  </Document>
  );
};

export default UsedCarPDF;
